/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { compact } from 'lodash';
import type {
  CoordinateReferenceSystem,
  Dimension,
  GeographicBoundingBox,
  LayerProps,
  Style,
  WMSLayerFromGetCapabilities,
} from './types';
import {
  WMDateOutSideRange,
  WMDateTooEarlyString,
  WMDateTooLateString,
} from './WMConstants';
import type WMLayer from './WMLayer';
import { getCorrectWMSDimName } from '../utils/utils';

/* debug helper */
export const enableConsoleDebugging = false;
export enum DebugType {
  Log,
  Warning,
  Error,
}
export const debugLogger = (
  type: DebugType,
  message: string,
  ...optionalParam: unknown[]
): void => {
  if (enableConsoleDebugging) {
    switch (type) {
      case DebugType.Log:
        // eslint-disable-next-line no-console
        console.log(`Log: ${message}`, optionalParam);
        break;
      case DebugType.Warning:
        console.warn(`Warning: ${message}`, optionalParam);
        break;
      case DebugType.Error:
        console.error(`Error: ${message}`, optionalParam);
        break;
      default:
        break;
    }
  }
};

/**
 * Checks if variable is defined or not
 * @param variable The variable to check
 * @returns true if variable is indeed defined, otherwise false.
 */
export const isDefined = <T>(variable?: T): variable is T => {
  if (variable === null) {
    return false;
  }
  if (typeof variable === 'undefined') {
    return false;
  }
  return true;
};

/**
 * Converts a variable to an array. If the variable is not an array it will be pushed as the first entry in a new array. If the variable is already an array, nothing will  be done.
 * @param array The variable to convert
 * @returns Always an array
 */
export const toArray = <T>(array: T | T[] | null | undefined): T[] => {
  if (array === null || typeof array === 'undefined') {
    return [];
  }
  if (Array.isArray(array)) {
    return array;
  }
  return [array];
};

/* Returns all dimensions with its current values as URL */
export const getMapDimURL = (
  layer: WMLayer,
  dimensionOverride?: Dimension[],
): URLSearchParams => {
  const request = new URLSearchParams();
  layer.dimensions.forEach((layerDimension) => {
    const overrideDim = dimensionOverride?.find((dim) => {
      return dim.name === layerDimension.name;
    });
    const dimension = layerDimension;

    const currentValue =
      (overrideDim &&
        dimension.getClosestValue(overrideDim.currentValue, true)) ||
      dimension.currentValue;
    request.append(getCorrectWMSDimName(dimension.name), currentValue);
    if (
      currentValue === WMDateOutSideRange ||
      currentValue === WMDateTooEarlyString ||
      currentValue === WMDateTooLateString
    ) {
      throw new Error(WMDateOutSideRange);
    }
  });
  return request;
};

/**
 * Parses url and then it it allows for setting / changing key value pairs in that URL (From https://stackoverflow.com/questions/5999118/how-can-i-add-or-update-a-query-string-parameter)
 * @param baseUrl
 * @param params
 * @returns
 */
export const getUriWithParam = (
  baseUrl: string,
  params?: Record<string, unknown>,
): string => {
  const url = new URL(baseUrl);
  const urlParams: URLSearchParams = new URLSearchParams(url.search);
  if (params) {
    for (const key in params) {
      if (params[key] !== undefined) {
        urlParams.set(key, String(params[key]));
      }
    }
  }
  url.search = urlParams.toString();
  return url.toString();
};

/**
 * Parses url and then it it allows for setting additional key value pairs in that URL (From https://stackoverflow.com/questions/5999118/how-can-i-add-or-update-a-query-string-parameter)
 * @param baseUrl
 * @param params
 * @returns
 */
export const getUriAddParam = (
  baseUrl: string,
  params: Record<string, unknown>,
): string => {
  const url = new URL(baseUrl);
  const urlParams: URLSearchParams = new URLSearchParams(url.search);
  // Create search params all lowercase so we can compare to the params passed in
  const lowerCaseUrlParams = new URLSearchParams();
  for (const key of urlParams.keys()) {
    lowerCaseUrlParams.append(key.toLowerCase(), 'tempval');
  }
  Object.keys(params).forEach((key) => {
    if (
      params[key] !== undefined &&
      !lowerCaseUrlParams.has(key.toLowerCase())
    ) {
      urlParams.set(key, String(params[key]));
    }
  });
  url.search = urlParams.toString();
  return url.toString();
};

/**
 * Helper function to figure out the styles from the WMS layer object from the WMS GetCapabilities document
 * @param layerObjectFromWMS The layer object from the WMS GetCapabilities JSON document
 * @returns Style[] Array of style objects.
 */

export const addStylesForLayer = (
  layerObjectFromWMS: WMSLayerFromGetCapabilities,
): Style[] => {
  /* Get the Style object */
  if (
    !layerObjectFromWMS?.Style ||
    (typeof layerObjectFromWMS.Style === 'object' &&
      Object.keys(layerObjectFromWMS.Style).length === 0)
  ) {
    return [];
  }
  const layerStyles = toArray(layerObjectFromWMS.Style);

  if (!layerStyles) {
    return [];
  }

  /* Loop through the list of styles from the document, create a default object and try to fill in the object with the props from the WMS GetCapabilities document */
  interface WMSStyle {
    Title?: { value: string };
    Name?: { value: string };
    LegendURL?: { OnlineResource: { attr: { [key: string]: string } } };
    Abstract?: { value: string };
  }

  return layerStyles.map((layerStyle: WMSStyle) => {
    const style: Style = {
      title: 'default',
      name: 'default',
      legendURL: '',
      abstract: 'No abstract available',
    };

    const getValueOrDefault = (
      obj: WMSStyle | undefined,
      key: keyof WMSStyle,
      defaultValue: string,
    ): string => {
      // Only return .value if obj[key] is an object with a 'value' property
      const prop = obj?.[key];
      if (
        prop &&
        typeof prop === 'object' &&
        'value' in prop &&
        typeof prop.value === 'string'
      ) {
        return prop.value;
      }
      return defaultValue;
    };

    try {
      style.title = getValueOrDefault(layerStyle, 'Title', style.title);
    } catch (_e) {
      /* Do nothing */
    }
    try {
      style.name = layerStyle.Name?.value ? layerStyle.Name.value : style.name;
    } catch (_e) {
      /* Do nothing */
    }
    try {
      style.legendURL =
        layerStyle.LegendURL?.OnlineResource?.attr['xlink:href'] ?? '';
    } catch (_e) {
      /* Do nothing */
    }
    try {
      style.abstract = layerStyle.Abstract?.value ?? style.abstract;
    } catch (_e) {
      /* Do nothing */
    }

    return style;
  });
};

interface WMSDimension {
  attr: {
    name: string;
    units: string;
    default: string;
    unitSymbol: string;
  };
  value: string;
}

interface WMSExtent {
  attr: {
    name: string;
    default?: string;
  };
  value: string;
}

export const addDimensionsForLayer = (
  layerObjectFromWMS: WMSLayerFromGetCapabilities,
): Dimension[] => {
  if (!layerObjectFromWMS?.Dimension) {
    return [];
  }
  const layerDimensions = toArray(
    layerObjectFromWMS.Dimension,
  ) as WMSDimension[];
  const layerDimensionsExtents = toArray(
    layerObjectFromWMS.Extent,
  ) as WMSExtent[];

  const dimensionList = layerDimensions.map((layerDimension) => {
    let dimensionName = '';
    if (layerDimension.attr?.name) {
      dimensionName = layerDimension.attr.name.toLowerCase();
    }

    /* Find corresponding Dimension Extent if given */
    const dimExtentIndex = layerDimensionsExtents.findIndex(
      (layerDimensionsExtent) => {
        const extentName = layerDimensionsExtent.attr?.name?.toLowerCase();
        return dimensionName === extentName;
      },
    );
    const dimExtent =
      dimExtentIndex !== -1 ? layerDimensionsExtents[dimExtentIndex] : null;

    const dimensionValues = dimExtent?.value || layerDimension.value || '';

    const dimensionCurrentValue =
      dimExtent?.attr?.default || layerDimension.attr.default || '';

    return {
      name: dimensionName,
      units: layerDimension.attr.units,
      currentValue: dimensionCurrentValue.trim(),
      unitSymbol: layerDimension.attr.unitSymbol,
      values: dimensionValues.trim(),
    };
  });
  return dimensionList;
};

export const addBoundingBoxForLayer = (
  layerObjectFromWMS: WMSLayerFromGetCapabilities,
): GeographicBoundingBox | undefined => {
  if (!layerObjectFromWMS) {
    return undefined;
  }
  if (layerObjectFromWMS.EX_GeographicBoundingBox) {
    const layerBbox = layerObjectFromWMS.EX_GeographicBoundingBox;
    return {
      east: layerBbox.eastBoundLongitude?.value,
      west: layerBbox.westBoundLongitude?.value,
      north: layerBbox.northBoundLatitude?.value,
      south: layerBbox.southBoundLatitude?.value,
    };
  }

  if (layerObjectFromWMS.LatLonBoundingBox) {
    const layerBbox = layerObjectFromWMS.LatLonBoundingBox;
    return {
      east: layerBbox.attr.maxx,
      west: layerBbox.attr.minx,
      north: layerBbox.attr.maxy,
      south: layerBbox.attr.miny,
    };
  }

  return undefined;
};

/**
 * Sorts an array with objects by object key.
 *
 * @param array Array of objects
 * @param key The key of the object to use for sorting
 * @returns Sorted array.
 */
export function sortArrayOfObjectsByKey<T>(array: T[], key: keyof T): T[] {
  return [...array].sort((arrayA, arrayB) => {
    const x = arrayA[key];
    const y = arrayB[key];
    if (x < y) {
      return -1;
    }
    if (x > y) {
      return 1;
    }
    return 0;
  });
}

/**
 * Makes a LayerProps object from the WMSLayerFromGetCapabilities and nestedLayerPath
 * @param layer WMSLayerFromGetCapabilities
 * @param path The path of the layer in the WMS GetCapabilities tree (Array of strings)
 * @param isleaf Is this a group layer or an actual layer
 * @param nestedLayerPath The path of the layer in the WMS GetCapabilities, in this case array of LayerProps
 * @returns
 */
export const makeNodeLayerFromWMSGetCapabilityLayer = (
  layer: WMSLayerFromGetCapabilities,
  path: string[] = [],
  isleaf = false,
  nestedLayerPath: LayerProps[] = [],
): LayerProps => {
  // geographicBoundingBox, dimensions and styles resolve nearest-parent-first, so reduce over
  // a reversed copy of nestedLayerPath without mutating the original parameter
  const reversedLayerPath = nestedLayerPath.slice().reverse();

  const geographicBoundingBox: GeographicBoundingBox | undefined =
    reversedLayerPath.reduce((npath, lprops) => {
      return npath || lprops.geographicBoundingBox;
    }, addBoundingBoxForLayer(layer));

  const dimensions: Dimension[] = reversedLayerPath.reduce((dims, props) => {
    const parentDims = (props.dimensions || []).filter(
      (dim) => !dims.some((dimit) => dimit.name === dim.name),
    );
    return [...parentDims, ...dims];
  }, addDimensionsForLayer(layer));

  const styles: Style[] = reversedLayerPath.reduce((npath, lprops) => {
    if (lprops.styles && Array.isArray(lprops.styles) && lprops.styles.length) {
      return lprops.styles.concat(npath);
    }
    return npath;
  }, addStylesForLayer(layer));

  const queryable = layer?.attr
    ? parseInt(layer.attr.queryable, 10) === 1
    : false;

  const crs: CoordinateReferenceSystem[] =
    layer?.BoundingBox &&
    Array.isArray(layer.BoundingBox) &&
    layer.BoundingBox.length
      ? compact(
          layer.BoundingBox.map(
            (wmsBBOX): CoordinateReferenceSystem | undefined => {
              if (wmsBBOX.attr) {
                const crsName = wmsBBOX.attr.CRS || wmsBBOX.attr.SRS;
                return {
                  name: crsName,
                  bbox: {
                    left: parseFloat(wmsBBOX.attr.minx),
                    right: parseFloat(wmsBBOX.attr.maxx),
                    bottom: parseFloat(wmsBBOX.attr.miny),
                    top: parseFloat(wmsBBOX.attr.maxy),
                  },
                };
              }
              return undefined;
            },
          ),
        )
      : [];

  const layerName = layer?.Name ? layer.Name.value : null;
  const layerTitle = layer?.Title ? layer.Title.value : layerName;

  return {
    name: layerName,
    title: layerTitle || undefined,
    leaf: isleaf,
    path,
    keywords: toArray(layer?.KeywordList?.Keyword ?? [])
      .map((keyword) =>
        typeof keyword === 'object' && keyword !== null && 'value' in keyword
          ? (keyword as { value: string }).value
          : String(keyword),
      )
      .filter(Boolean),
    abstract: layer?.Abstract ? layer.Abstract.value : undefined,
    styles,
    dimensions,
    geographicBoundingBox,
    queryable,
    crs,
  };
};

export const getFirstPartOfDimensionValueSet = (inputValue: string): string => {
  return !inputValue?.indexOf || !inputValue?.includes('/')
    ? inputValue
    : inputValue.split('/')[0];
};
